/** The Linked repositories table's cells: repository, account-key chips, status, and the admin kebab. */
import { ExternalLink, LoaderCircle, Pencil, Unlink } from 'lucide-react';

import { KebabMenu } from '@/_shared/components/list/KebabMenu';
import { TruncateWithTooltip } from '@/_shared/components/TruncateWithTooltip';
import { Badge } from '@/_shared/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/_shared/components/ui/tooltip';

import { type LinkedProject, projectLabel } from '../../lib/linkedRepoList';

export function LinkedRepoActionsCell({
  project,
  onEdit,
  onUnlink,
}: {
  project: LinkedProject;
  onEdit: (project: LinkedProject) => void;
  onUnlink: (project: LinkedProject) => void;
}): React.JSX.Element {
  return (
    <KebabMenu
      label={`Actions for ${projectLabel(project)}`}
      actions={[
        { label: 'Edit', icon: Pencil, onSelect: () => onEdit(project) },
        { label: 'Unlink', icon: Unlink, destructive: true, onSelect: () => onUnlink(project) },
      ]}
    />
  );
}

/**
 * The repository name. Phrasing content only: the table renders this cell inside the row's activator
 * `<button>`, so the GitHub link sits beside it ({@link RepositoryLink}, the column's `cellAside`).
 */
export function RepositoryCell({ project }: { project: LinkedProject }): React.JSX.Element {
  const slash = project.repoFullName.indexOf('/');
  return (
    <span className="block min-w-0 truncate" title={project.repoFullName}>
      {slash > 0 && <span className="text-muted-foreground">{project.repoFullName.slice(0, slash + 1)}</span>}
      <span className="font-medium text-foreground">
        {slash > 0 ? project.repoFullName.slice(slash + 1) : project.repoFullName}
      </span>
    </span>
  );
}

/** The repository's GitHub page, or nothing when its URL is unknown or not https. */
export function RepositoryLink({
  project,
  htmlUrl,
}: {
  project: LinkedProject;
  htmlUrl: string | null;
}): React.JSX.Element | null {
  if (!htmlUrl) return null;
  return (
    <a
      href={htmlUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${project.repoFullName} on GitHub (opens in a new tab)`}
      className="inline-flex shrink-0 rounded-sm text-muted-foreground focus-ring hover:text-foreground"
    >
      <ExternalLink className="size-3.5" aria-hidden />
    </a>
  );
}

/** Chips shown before the rest collapse into "+N". */
const VISIBLE_KEY_CHIPS = 2;

/**
 * One chip per key, by database. A Disconnected key's chip shows only the database, in the warning
 * colour, so the name keeps the chip's width; its tooltip says "Removed: no account for PROD" and
 * screen readers hear "PROD (removed)".
 */
export function AccountKeysCell({ project }: { project: LinkedProject }): React.JSX.Element {
  const shown = project.keys.slice(0, VISIBLE_KEY_CHIPS);
  const rest = project.keys.slice(VISIBLE_KEY_CHIPS);
  return (
    <span className="flex min-w-0 items-center gap-1 overflow-hidden">
      <span className="sr-only">Account keys: {project.keysLabel}</span>
      {shown.map((key) =>
        key.accountStatus === 'disconnected' ? (
          <Tooltip key={key.accountKey}>
            <TooltipTrigger asChild>
              <Badge variant="warning" tabIndex={0} className="min-w-0 shrink focus-ring">
                <span className="min-w-0 truncate">{key.display.database}</span>
                <span className="sr-only"> (removed)</span>
              </Badge>
            </TooltipTrigger>
            <TooltipContent>Removed: no account for {key.display.database}</TooltipContent>
          </Tooltip>
        ) : (
          <Badge key={key.accountKey} variant="outline" className="min-w-0 shrink">
            <TruncateWithTooltip focusable>{key.display.database}</TruncateWithTooltip>
          </Badge>
        ),
      )}
      {rest.length > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="outline" tabIndex={0} className="shrink-0 focus-ring">
              +{rest.length}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            {rest
              .map((k) => (k.accountStatus === 'disconnected' ? `${k.display.database} (removed)` : k.display.database))
              .join(', ')}
          </TooltipContent>
        </Tooltip>
      )}
    </span>
  );
}

export function LinkedRepoStatusCell({ project }: { project: LinkedProject }): React.JSX.Element {
  const disconnected = project.keys.filter((k) => k.accountStatus === 'disconnected').length;
  return (
    <span className="flex min-w-0 flex-col items-start gap-1">
      {project.status === 'mixed' ? (
        // One badge per part, stacked: "1 connected · 1 disconnected" on one badge wraps mid-phrase.
        <>
          <Badge variant="success">{project.keys.length - disconnected} connected</Badge>
          <Badge variant="warning">{disconnected} disconnected</Badge>
        </>
      ) : (
        <Badge variant={project.status === 'connected' ? 'success' : 'warning'}>{project.statusSummary}</Badge>
      )}
      {project.anyDocBusy && (
        <span role="status" className="flex items-center gap-1 text-xs text-muted-foreground">
          <LoaderCircle className="size-3 animate-spin" aria-hidden />
          Generating docs
        </span>
      )}
    </span>
  );
}
